import React, { useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Mail, Send } from "lucide-react";

// Emails the consignment note PDF + any captured POD photos in one message —
// for customers who don't use the client portal. Invoices are sent separately
// from the Invoicing tab.
export default function SendCnPodModal({ job, defaultTo, defaultCc, podCount, onClose, onSent }) {
  const [to, setTo] = useState(defaultTo || "");
  const [cc, setCc] = useState(defaultCc || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (to.trim()) payload.to = to.trim();
      if (cc.trim()) payload.cc = cc.trim();
      if (message.trim()) payload.message = message.trim();
      const { data } = await api.post(`/jobs/${job.id}/send-consignment-note`, payload);
      if (data.sent) { toast.success("Consignment note & POD emailed"); onSent && onSent(); }
      else toast.error(data.reason || "Not sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0b1430] border border-white/10 rounded-2xl w-full max-w-md my-auto" data-testid="send-cn-pod-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="brand-title text-xl flex items-center gap-2"><Mail size={17} className="text-emerald-400" /> Send CN &amp; POD — {job.job_ref}</h3>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white"><X size={20} /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-xs text-white/50">
            Emails the consignment note PDF{podCount > 0 ? ` and ${podCount} proof-of-delivery photo(s)` : ""} in one message. Invoices are sent separately from the Invoicing tab.
          </p>
          <div>
            <label className="block text-white/85 text-sm mb-1">Recipient(s)</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} className="df-input-rect" placeholder="customer@example.com, driver@example.com" data-testid="send-cn-pod-to" />
            {!defaultTo && <p className="text-[10px] text-amber-300 mt-1">No driver/customer email on file — enter one above.</p>}
          </div>
          <div>
            <label className="block text-white/85 text-sm mb-1">CC (optional)</label>
            <input value={cc} onChange={(e) => setCc(e.target.value)} className="df-input-rect" placeholder="cc1@example.com, cc2@example.com" data-testid="send-cn-pod-cc" />
          </div>
          <div>
            <label className="block text-white/85 text-sm mb-1">Message (optional)</label>
            <textarea rows="3" value={message} onChange={(e) => setMessage(e.target.value)} className="df-input-rect resize-none" placeholder="Added to the email body" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 text-sm font-semibold">Cancel</button>
          <button onClick={send} disabled={busy} className="px-5 py-2.5 rounded-lg text-white font-semibold flex items-center gap-2 disabled:opacity-50" style={{ background: "linear-gradient(90deg,#22c55e,#16a34a)" }} data-testid="confirm-send-cn-pod-btn">
            <Send size={15} /> {busy ? "Sending…" : "Send CN & POD"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
